import { Badge, Button, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner, TabContent, TabPane } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import {
  IoAttachOutline, IoCalendarOutline, IoCopyOutline, IoCreateOutline, IoLocationOutline,
  IoOpenOutline, IoPeopleOutline, IoPersonOutline, IoReaderOutline, IoTimeOutline, IoTrashOutline
} from "react-icons/io5";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get, post } from "../../../../utils/helpers/api";
import OverviewTab from "./Tabs/Overview";
import Timeline from "../../components/Timeline";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import { formatMinutes, minutesBetween, personName } from "../../utils/helpers";
import {
  Avatar, Fact, RecordLayout, RecordMain, RecordNav, RecordRail, RecordTabs, StatStrip, useScrollSpy
} from "../../components/Record";

function ViewMeetingPage() {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useSettings();
  const NAV_SECTIONS = [
    { id: 'meeting-informations-card', label: t('crm.meetingInformationsSection'), icon: <IoCalendarOutline size={14} /> },
    { id: 'participants-informations-card', label: t('crm.participants'), icon: <IoPeopleOutline size={14} /> },
    { id: 'notes-card', label: t('common.notes'), icon: <IoReaderOutline size={14} /> },
    { id: 'attachments-card', label: t('attachments.title'), icon: <IoAttachOutline size={14} /> }
  ];
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [meetingLoading, setMeetingLoading] = useState(true);
  const [meetingData, setMeetingData] = useState(state?.meetingData || {});
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { activeSection, scrollToSection } = useScrollSpy(
    NAV_SECTIONS.map(section => section.id),
    scrollContainerRef,
    activeTab === 'overview',
    meetingData?.id
  );

  useEffect(() => {
    if (id) {
      getMeeting();
    }
  }, [id]);

  const getMeeting = async () => {
    setMeetingLoading(true);
    const data = await get('/crm/meetings/view/' + id);
    if (data?.meeting) {
      setMeetingData(data.meeting);
      setMeetingLoading(false);
    }
  };

  const cloneMeeting = async () => {
    const data = await post('/crm/meetings/clone/' + id);
    if (data?.meeting) {
      navigate('/crm/meetings/edit/' + data.meeting.id, { state: { meetingData: data.meeting } });
    }
  };

  const deleteMeeting = async () => {
    const data = await del('/crm/meetings/delete/' + id);
    if (data?.success) {
      toast.success(data.message || t('crm.meetingDeleted'));
      navigate('/crm/meetings');
    }
  };

  const title = meetingData?.title || t('crm.meeting');
  const customer = meetingData?.customer;
  const customerName = customer?.company_name || personName(customer);
  const relatedTo = customer?.id
    ? `/crm/customers/view/${customer.id}`
    : meetingData?.lead_id ? `/crm/leads/view/${meetingData.lead_id}` : null;
  const duration = minutesBetween(meetingData?.time_from, meetingData?.time_to);
  const timeRange = [meetingData?.time_from, meetingData?.time_to]
    .filter(Boolean)
    .map(value => formatTime(value))
    .join(' – ');
  const participants = meetingData?.participants ?? [];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={title}
          onBack={() => navigate('/crm/meetings')}
        >
          <Button
            size="sm"
            outline
            color="secondary"
            className="d-flex align-items-center gap-1"
            onClick={() => navigate(`/crm/meetings/edit/${id}`, { state: { meetingData } })}
          >
            <IoCreateOutline size={14} />
            {t('common.edit')}
          </Button>
          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={cloneMeeting}>
                <IoCopyOutline size={15} /> {t('common.clone')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable" ref={scrollContainerRef}>
          {meetingLoading ?
            <Spinner color="primary" className="d-block mx-auto my-4" />
          :
            <RecordLayout>
              <RecordRail>
                <Card>
                  <CardBody className="text-center pb-3">
                    <Avatar fallback={<IoCalendarOutline size={26} />} />

                    <h6 className="fw-bold mb-2 mt-3">{title}</h6>
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      {meetingData?.location && <Badge color="light">{meetingData.location}</Badge>}
                      {meetingData?.time_from && <Badge color="info">{formatDate(meetingData.time_from)}</Badge>}
                    </div>

                    <div className="d-flex justify-content-center gap-2 mt-3">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!relatedTo}
                        onClick={() => navigate(relatedTo)}
                      >
                        <IoOpenOutline size={14} />
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={() => scrollToSection('participants-informations-card')}
                      >
                        <IoPeopleOutline size={14} />
                      </Button>
                    </div>
                  </CardBody>

                  <hr className="my-0" />

                  <CardBody className="d-flex flex-column gap-2">
                    <Fact icon={<IoPersonOutline size={14} />} value={personName(meetingData?.host)} />
                    <Fact icon={<IoOpenOutline size={14} />} value={customerName} />
                    <Fact icon={<IoLocationOutline size={14} />} value={meetingData?.location} />
                    <Fact icon={<IoTimeOutline size={14} />} value={timeRange || null} />
                    <Fact icon={<IoPeopleOutline size={14} />} value={participants.length || null} />
                  </CardBody>
                </Card>

                {activeTab === 'overview' && (
                  <RecordNav sections={NAV_SECTIONS} activeId={activeSection} onSelect={scrollToSection} />
                )}
              </RecordRail>

              <RecordMain>
                <StatStrip
                  tiles={[
                    {
                      icon: <IoCalendarOutline size={18} />,
                      background: '#f0eeff',
                      color: '#6C63FF',
                      value: meetingData?.time_from ? formatDate(meetingData.time_from) : '-',
                      label: t('common.date')
                    },
                    {
                      icon: <IoTimeOutline size={18} />,
                      background: '#e6f4ff',
                      color: '#1d7afc',
                      value: timeRange || '-',
                      label: t('crm.startTime')
                    },
                    {
                      icon: <IoTimeOutline size={18} />,
                      background: '#fff3e6',
                      color: '#fd7e14',
                      value: formatMinutes(duration) || '-',
                      label: t('common.duration')
                    },
                    {
                      icon: <IoPeopleOutline size={18} />,
                      background: '#e9f9f0',
                      color: '#22a06b',
                      value: participants.length,
                      label: t('crm.participants')
                    }
                  ]}
                />

                <RecordTabs
                  tabs={[
                    { id: 'overview', label: t('crm.overview') },
                    { id: 'timeline', label: t('crm.timeline') }
                  ]}
                  activeTab={activeTab}
                  onChange={setActiveTab}
                />
                <TabContent activeTab={activeTab} className="border-0 p-0">
                  <TabPane tabId="overview">
                    <OverviewTab meetingData={meetingData} />
                  </TabPane>
                  <TabPane tabId="timeline">
                    <Timeline subjectType="meeting" subjectId={id} />
                  </TabPane>
                </TabContent>
              </RecordMain>
            </RecordLayout>
          }
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        onConfirm={deleteMeeting}
        title={t('crm.deleteMeeting')}
        message={t('crm.deleteMeetingConfirm')}
      />
    </SidebarLayout>
  );
}

export default ViewMeetingPage;
